/**
 * Mobile drawer destination search autocomplete. Same filtering approach as
 * assets/je-hero.js (render every destination server-side once, then
 * show/hide on a text match) — kept as its own file rather than sharing
 * je-hero.js directly since that file resolves its elements by a single
 * hardcoded #JeHeroSearch id, and the drawer needs its own independently
 * scoped instance (#JeDrawerSearch) that can coexist with the hero's on the
 * same page (homepage has both).
 */
document.addEventListener('DOMContentLoaded', () => {
  const input = document.getElementById('JeDrawerSearch');
  if (!input) return;
  const form = input.closest('form');
  const pill = input.closest('.je-hero__search-pill');
  const dropdown = pill ? pill.querySelector('[data-je-drawer-dropdown]') : null;
  if (!form || !pill || !dropdown) return;

  const popularTitle = dropdown.querySelector('.je-hero__autocomplete-topbar');
  const popularGrid = dropdown.querySelector('[data-je-drawer-popular-grid]');
  const allGrid = dropdown.querySelector('[data-je-drawer-all-grid]');
  const emptyMsg = dropdown.querySelector('[data-je-drawer-empty]');
  const closeBtn = dropdown.querySelector('[data-je-drawer-close]');
  const allCards = Array.from(allGrid.querySelectorAll('[data-je-hero-dest]'));

  let activeIndex = -1;
  let visibleCards = [];

  function selectDestination(card) {
    const url = card.getAttribute('data-je-hero-dest-url');
    if (url) window.location.href = url;
  }

  function closeDropdown() {
    dropdown.hidden = true;
    activeIndex = -1;
  }

  function setActiveOption(index) {
    visibleCards.forEach((card, idx) => {
      const isActive = idx === index;
      card.classList.toggle('je-hero__autocomplete-popular-btn--active', isActive);
      card.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) input.setAttribute('aria-activedescendant', card.id);
    });
    activeIndex = index;
  }

  function filterDestinations(query) {
    const q = query.trim().toLowerCase();
    dropdown.hidden = false;

    if (!q) {
      if (popularTitle) popularTitle.hidden = false;
      popularGrid.hidden = false;
      allGrid.hidden = true;
      if (emptyMsg) emptyMsg.hidden = true;
      visibleCards = Array.from(popularGrid.querySelectorAll('[data-je-hero-dest]'));
      visibleCards.forEach((card, idx) => { card.id = `je-drawer-autocomplete-option-${idx}`; });
      setActiveOption(-1);
      return;
    }

    if (popularTitle) popularTitle.hidden = true;
    popularGrid.hidden = true;
    allGrid.hidden = false;

    visibleCards = [];
    allCards.forEach((card) => {
      const name = card.getAttribute('data-je-hero-dest-name').toLowerCase();
      const match = name.includes(q);
      card.hidden = !match;
      if (match) visibleCards.push(card);
    });
    visibleCards.forEach((card, idx) => { card.id = `je-drawer-autocomplete-option-${idx}`; });

    if (emptyMsg) emptyMsg.hidden = visibleCards.length !== 0;
    setActiveOption(-1);
  }

  allCards.forEach((card) => {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      selectDestination(card);
    });
  });
  Array.from(popularGrid.querySelectorAll('[data-je-hero-dest]')).forEach((card) => {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      selectDestination(card);
    });
  });

  // Below 750px, assets/je-search-full.js marks this input readonly and
  // opens a full-screen search overlay instead — skip opening this small
  // inline dropdown underneath it too.
  input.addEventListener('focus', () => {
    if (input.readOnly) return;
    filterDestinations(input.value);
  });
  input.addEventListener('input', () => filterDestinations(input.value));

  // type="search" inputs fire a native 'search' event when the browser's
  // built-in clear (x) button is clicked, separately from the 'input'
  // event above — clearing the field that way should close the dropdown
  // entirely rather than fall back to showing Popular Destinations again.
  input.addEventListener('search', () => {
    if (!input.value.trim()) closeDropdown();
  });

  if (closeBtn) {
    // Just closes the dropdown, not the whole drawer/menu — input.blur()
    // was removed here because it fires Dawn's own onFocusOut listener on
    // the parent <header-drawer> (assets/global.js), which closes the
    // entire mobile menu once focus leaves it with nothing to replace it.
    closeBtn.addEventListener('click', () => closeDropdown());
  }

  document.addEventListener('click', (e) => {
    if (!pill.contains(e.target)) closeDropdown();
  });

  input.addEventListener('keydown', (e) => {
    if (dropdown.hidden) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        filterDestinations(input.value);
        e.preventDefault();
      }
      return;
    }
    if (visibleCards.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveOption((activeIndex + 1) % visibleCards.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveOption(activeIndex <= 0 ? visibleCards.length - 1 : activeIndex - 1);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < visibleCards.length) {
        e.preventDefault();
        selectDestination(visibleCards[activeIndex]);
      } else {
        const exact = visibleCards.find(
          (card) => card.getAttribute('data-je-hero-dest-name').toLowerCase() === input.value.trim().toLowerCase()
        );
        if (exact) {
          e.preventDefault();
          selectDestination(exact);
        }
      }
    } else if (e.key === 'Escape') {
      closeDropdown();
      input.blur();
    }
  });

  form.addEventListener('submit', (e) => {
    const query = input.value.trim();
    if (!query) {
      e.preventDefault();
      return;
    }
    const exact = allCards.find(
      (card) => card.getAttribute('data-je-hero-dest-name').toLowerCase() === query.toLowerCase()
    );
    if (exact) {
      e.preventDefault();
      selectDestination(exact);
    }
  });
});
